import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import './ContactPage.css'
import logoImage from '../images/morecharz-round-logo.webp'
import WhatsAppImage from '../images/whatsapp-chat.svg'
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL
function ContactPage({ user }) {
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    subject: '',
    message: ''
  })
  const [submitted, setSubmitted] = useState(false)
  const [ticketId, setTicketId] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const nameInputRef = useRef(null)

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      name: user?.name || prev.name || '',
      email: user?.email || prev.email || '',
      phone: user?.phone || prev.phone || ''
    }))
  }, [user])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      setIsSubmitting(true)
      setSubmitError('')

      const response = await fetch(`${apiBaseUrl}/contact/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      const result = await response.json()
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to send message')
      }

      setSubmitted(true)
      setTicketId(result.ticketId || '')
      window.scrollTo({ top: 0, behavior: 'smooth' })

      setFormData(prev => ({
        ...prev,
        subject: '',
        message: ''
      }))
    } catch (error) {
      setSubmitError(error.message || 'Failed to send message. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSendMessageClick = (e) => {
    e.preventDefault()
    setIsSubmitting(false)
    setSubmitted(false)
    setTicketId('')
    setSubmitError('')
    setFormData(prev => ({
      ...prev,
      subject: '',
      message: ''
    }))

    setTimeout(() => {
      if (nameInputRef.current) {
        nameInputRef.current.focus()
      }
    }, 0)

    const formSection = document.getElementById('contact-form')
    if (formSection) {
      formSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }

    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="contact-page">
      <div className="contact-hero">
        <div className="container">
          <h1>Get in Touch</h1>
          <p>Have questions? We'd love to hear from you. Send us a message!</p>
        </div>
      </div>
      <div className="container">
        <div className="contact-content">
          <div className="contact-form-section" id="contact-form">
            {submitted ? (
              <div className="success-message">
                <span className="material-icons">check_circle</span>
                <h3>Thank You!</h3>
                <p>Your message has been sent successfully. We'll get back to you soon.</p>
                {ticketId && <p className="ticket-id">Your reference code is <strong>{ticketId}</strong>.</p>}
                <button type="button" className="success-action-button" onClick={handleSendMessageClick}>
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-form">
                {user && (
                  <p className="profile-details-note">
                    Your contact details come from your profile. <Link to="/profile">Update them in My Profile</Link>.
                  </p>
                )}
                {submitError && <p style={{ color: '#dc2626', marginBottom: '1rem' }}>{submitError}</p>}
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    ref={nameInputRef}
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your name"
                    disabled={Boolean(user)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    disabled={Boolean(user)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Your 10-digit number"
                    maxLength="10"
                    pattern="[0-9]{10}"
                    disabled={Boolean(user)}
                  />
                </div>

                <div className="form-group">
                  <label>Subject</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="What is this about?"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Your message here..."
                    rows="6"
                    required
                  ></textarea>
                </div>

                <button type="submit" className="btn-submit" disabled={isSubmitting}>
                  <span className="material-icons">send</span>
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>

                {isSubmitting && (
                  <div className="mail-processing-indicator" aria-live="polite">
                    <span className="mail-spinner-mark" aria-hidden="true">
                      <span className="mail-spinner-ring" />
                      <img src={logoImage} alt="" className="mail-spinner-image" />
                    </span>
                    <p>Processing your message...</p>
                  </div>
                )}
              </form>
            )}
          </div>

          <div className="contact-info-section">
            <div className="info-card">
              <div className="info-icon">
                <span className="whatsapp-contact-icon" role="img" aria-label="WhatsApp" />
              </div>
              <h3>WhatsApp</h3>
              <p><a href="https://wa.me/917655062254" target="_blank" rel="noopener noreferrer">+91 76550 62254</a></p>
              <a className="info-link-button" href="https://wa.me/917655062254" target="_blank" rel="noopener noreferrer"><img src={WhatsAppImage} alt="Chat on WhatsApp" className="whatsapp-chat-img" /></a>
            </div>

            <div className="info-card">
              <div className="info-icon">
                <span className="material-icons">email</span>
              </div>
              <h3>Email</h3>
              <p><a href="mailto:support@morecharz.com">support@morecharz.com</a></p>
              <a className="info-link-button" href="mailto:support@morecharz.com" target="_blank" rel="noopener noreferrer">Send an email</a>
            </div>

            <div className="info-card">
              <div className="info-icon">
                <span className="material-icons">schedule</span>
              </div>
              <h3>Hours</h3>
              <p>24/7 Available<br />We respond within 24 hours</p>
            </div>

            <div className="info-card">
              <div className="info-icon">
                <span className="material-icons">location_on</span>
              </div>
              <h3>Address</h3>
              <p>MoRecharz (OPC) Private Limited</p>
              <p>Patuli, Olaver, Kendrapada, Odisha 754227, India</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ContactPage
